import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download, Database, Loader2, RefreshCw } from 'lucide-react';
import { Heading } from '../layout';
import { useUIColors } from '../../hooks/useTheme';
import { apiClient, API_BASE_URL } from '../../services/apiClient';
import AdministrationBackLink from '../system/AdministrationBackLink';
import {
  DUMP_FORMAT,
  downloadTextFile,
  mergeDumps,
  summarizeDump,
  withRetries,
} from '../system/datos/dataTransferCatalog';

/** CloudFront/proxies suelen cortar el dump completo; exportar de a 1 tabla evita la respuesta HTML. */
const EXPORT_BATCH_SIZE = 1;
/** Tablas con más filas que esto se piden por tramos (una sola tabla también puede superar el límite). */
const EXPORT_CHUNK_ROWS = 2000;

const formatPk = (pk = {}) => {
  const entries = Object.entries(pk || {});
  if (!entries.length) return 'sin PK';
  return entries.map(([k, v]) => `${k}=${v}`).join(', ');
};

const suggestFixSql = (item) => {
  const pk = item?.primary_key || {};
  if (!item?.table || !item?.column || pk.id == null) return null;
  return `UPDATE ${item.table} SET ${item.column} = 'YYYY-MM-DD' WHERE id = ${pk.id};`;
};

const filterHealthForTables = (health, tableNames) => {
  if (!health) return null;
  if (!tableNames || !tableNames.length) return health;
  const wanted = new Set(tableNames);
  const invalid = (health.invalid || []).filter((item) => wanted.has(item.table));
  const suspicious = (health.suspicious || []).filter((item) => wanted.has(item.table));
  return {
    ...health,
    invalid,
    suspicious,
    invalid_count: invalid.length,
    suspicious_count: suspicious.length,
  };
};

const DateHealthWarning = ({ health, ui, title }) => {
  const invalid = health?.invalid || [];
  const count = Number(health?.invalid_count || invalid.length || 0);
  if (!count) return null;

  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        borderRadius: 8,
        border: `1px solid ${ui.danger || '#dc2626'}`,
        background: 'rgba(220, 38, 38, 0.06)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <AlertTriangle size={16} color={ui.danger || '#dc2626'} style={{ marginTop: 2, flexShrink: 0 }} />
        <p style={{ margin: 0, fontSize: 13, color: ui.danger || '#dc2626', fontWeight: 700, lineHeight: 1.4 }}>
          {title || `No se puede exportar: ${count} fecha(s) corrupta(s)`}
        </p>
      </div>
      <p style={{ margin: '0 0 8px', fontSize: 12, color: ui.text, lineHeight: 1.45 }}>
        Corrige estos valores en la base (año fuera de 1–9999) y vuelve a intentar:
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520, fontSize: 12 }}>
          <thead>
            <tr>
              {['Tabla', 'Columna', 'Valor', 'Año', 'Fila (PK)', 'SQL sugerido'].map((label) => (
                <th
                  key={label}
                  style={{
                    textAlign: 'left',
                    padding: '6px 8px',
                    color: ui.muted,
                    borderBottom: `1px solid ${ui.border}`,
                    fontWeight: 600,
                    fontSize: 10,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invalid.slice(0, 30).map((item, idx) => (
              <tr key={`${item.table}-${item.column}-${idx}`}>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${ui.border}`, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                  {item.table}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${ui.border}`, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                  {item.column}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${ui.border}`, fontWeight: 700 }}>
                  {item.value}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${ui.border}` }}>
                  {item.year ?? '—'}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${ui.border}`, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                  {formatPk(item.primary_key)}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${ui.border}`, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }}>
                  {suggestFixSql(item) || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {count > 30 ? (
        <p style={{ margin: '8px 0 0', fontSize: 11, color: ui.muted }}>
          Mostrando 30 de {count}. Revisa el resto en SQL o en <code>/api/system/data/date-health</code>.
        </p>
      ) : null}
    </div>
  );
};

const SystemDataExportWorkspace = ({ isDarkMode = false, showAdminBack = false }) => {
  const ui = useUIColors();
  void isDarkMode;

  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [exportDateHealth, setExportDateHealth] = useState(null);
  const [exportMode, setExportMode] = useState('all'); // all | selected
  const [selectedTables, setSelectedTables] = useState([]);
  const [tableFilter, setTableFilter] = useState('');

  const cardStyle = {
    border: `1px solid ${ui.border}`,
    borderRadius: '12px',
    background: ui.panel,
    padding: '14px 16px',
  };

  const tables = summary?.tables || [];

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    setError('');
    try {
      const data = await apiClient(`${API_BASE_URL}/api/system/data/summary`, {
        timeoutMs: 600000,
      });
      setSummary(data);
      const names = (data?.tables || []).map((row) => row.name);
      setSelectedTables((prev) => {
        if (!prev.length) return names;
        const available = new Set(names);
        const kept = prev.filter((name) => available.has(name));
        return kept.length ? kept : names;
      });
      if (data?.date_health?.invalid_count) {
        setExportDateHealth(data.date_health);
      } else {
        setExportDateHealth(null);
      }
    } catch (err) {
      setSummary(null);
      setError(err.message || 'No se pudo cargar el resumen de tablas.');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const extractDateHealth = (err) => {
    const detail = err?.detail;
    if (detail && typeof detail === 'object' && detail.date_health) {
      return detail.date_health;
    }
    return null;
  };

  const filteredTables = useMemo(() => {
    const q = tableFilter.trim().toLowerCase();
    if (!q) return tables;
    return tables.filter((row) => String(row.name || '').toLowerCase().includes(q));
  }, [tables, tableFilter]);

  const selectedCount = selectedTables.length;
  const allVisibleSelected =
    filteredTables.length > 0 && filteredTables.every((row) => selectedTables.includes(row.name));

  const toggleTable = (name) => {
    setSelectedTables((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };

  const selectAllVisible = () => {
    setSelectedTables((prev) => {
      const next = new Set(prev);
      filteredTables.forEach((row) => next.add(row.name));
      return Array.from(next);
    });
  };

  const clearVisible = () => {
    const visible = new Set(filteredTables.map((row) => row.name));
    setSelectedTables((prev) => prev.filter((name) => !visible.has(name)));
  };

  const handleExport = async () => {
    setBusy(true);
    setError('');
    setInfo('');
    setExportDateHealth(null);

    if (exportMode === 'selected' && !selectedTables.length) {
      setError('Selecciona al menos una tabla para exportar.');
      setBusy(false);
      return;
    }

    let currentStep = '';
    try {
      const tableNames =
        exportMode === 'all'
          ? (summary?.tables || []).map((row) => row.name).filter(Boolean)
          : [...selectedTables];

      if (!tableNames.length) {
        setError('No hay tablas para exportar. Actualiza el resumen e inténtalo de nuevo.');
        return;
      }

      const healthQuery = `?tables=${encodeURIComponent(tableNames.join(','))}`;
      try {
        const health = await apiClient(`${API_BASE_URL}/api/system/data/date-health${healthQuery}`, {
          timeoutMs: 600000,
        });
        if (health?.invalid_count > 0) {
          setExportDateHealth(health);
          setError(
            `Exportación bloqueada: hay ${health.invalid_count} fecha(s) corrupta(s) en el alcance elegido.`
          );
          return;
        }
      } catch (precheckErr) {
        if (precheckErr?.status && precheckErr.status !== 404) {
          const health = extractDateHealth(precheckErr);
          if (health?.invalid_count) {
            setExportDateHealth(health);
            setError(precheckErr.message || 'Fechas inválidas en la base.');
            return;
          }
        }
      }

      // Exportar por lotes evita que CloudFront/proxy convierta respuestas grandes en HTML.
      const rowCountByTable = new Map(
        (summary?.tables || []).map((row) => [row.name, Number(row.rows) || 0])
      );
      const batches = [];
      for (let i = 0; i < tableNames.length; i += EXPORT_BATCH_SIZE) {
        batches.push(tableNames.slice(i, i + EXPORT_BATCH_SIZE));
      }
      const estimatedRequests = batches.reduce((total, batch) => {
        const rows = batch.length === 1 ? rowCountByTable.get(batch[0]) || 0 : 0;
        return total + (rows > EXPORT_CHUNK_ROWS ? Math.ceil(rows / EXPORT_CHUNK_ROWS) : 1);
      }, 0);

      const exportRequest = (body) =>
        withRetries(
          () =>
            apiClient(`${API_BASE_URL}/api/system/data/export`, {
              method: 'POST',
              body: JSON.stringify(body),
              timeoutMs: 600000,
            }),
          {
            onRetry: (attempt, err, delayMs) => {
              setInfo(
                `Exportando ${currentStep}: el intento ${attempt} falló (${err.message}). ` +
                  `Reintentando en ${Math.round(delayMs / 1000)} s…`
              );
            },
          }
        );

      const partialDumps = [];
      let requestNumber = 0;
      for (const batch of batches) {
        const table = batch.length === 1 ? batch[0] : null;
        const chunked = table && (rowCountByTable.get(table) || 0) > EXPORT_CHUNK_ROWS;

        if (!chunked) {
          requestNumber += 1;
          currentStep = `lote ${requestNumber}/${estimatedRequests}: ${batch.join(', ')}`;
          setInfo(`Exportando ${currentStep}…`);
          partialDumps.push(await exportRequest({ tables: batch }));
          continue;
        }

        let tableDump = null;
        let offset = 0;
        for (;;) {
          requestNumber += 1;
          currentStep =
            `lote ${requestNumber}/${estimatedRequests}: ${table} ` +
            `(filas ${(offset + 1).toLocaleString('es-CL')}–` +
            `${(offset + EXPORT_CHUNK_ROWS).toLocaleString('es-CL')})`;
          setInfo(`Exportando ${currentStep}…`);
          const chunk = await exportRequest({
            tables: [table],
            row_offset: offset,
            row_limit: EXPORT_CHUNK_ROWS,
          });
          const rows = Array.isArray(chunk?.tables?.[table]) ? chunk.tables[table] : [];
          if (!tableDump) {
            tableDump = chunk;
          } else {
            tableDump.tables[table] = tableDump.tables[table].concat(rows);
          }
          if (rows.length < EXPORT_CHUNK_ROWS) break;
          offset += EXPORT_CHUNK_ROWS;
        }
        tableDump.row_counts = { ...(tableDump.row_counts || {}), [table]: tableDump.tables[table].length };
        partialDumps.push(tableDump);
      }
      currentStep = '';

      const dump = mergeDumps(partialDumps, {
        exportScope: exportMode === 'all' ? 'full' : 'partial',
      });

      const stats = summarizeDump(dump);
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const scope = dump?.export_scope === 'partial' ? 'parcial' : 'completa';
      downloadTextFile(
        `soquimat-db-dump-${scope}-${stamp}.json`,
        JSON.stringify(dump, null, 2),
        'application/json;charset=utf-8'
      );
      setInfo(
        `Exportadas ${stats.tableCount} tablas · ${stats.totalRows.toLocaleString('es-CL')} filas` +
          (dump?.export_scope === 'partial' ? ' (selección parcial).' : ' (por lotes).')
      );
      await loadSummary();
    } catch (err) {
      const health = extractDateHealth(err);
      if (health?.invalid_count) {
        setExportDateHealth(health);
      }
      const message = err.message || 'Error al exportar la base de datos.';
      setInfo('');
      setError(currentStep ? `Falló el ${currentStep}. ${message}` : message);
    } finally {
      setBusy(false);
    }
  };

  const displayedHealth = useMemo(() => {
    const base = exportDateHealth || summary?.date_health || null;
    if (exportMode === 'selected') {
      return filterHealthForTables(base, selectedTables);
    }
    return base;
  }, [exportDateHealth, summary, exportMode, selectedTables]);

  const hasBlockingDates = Number(displayedHealth?.invalid_count || 0) > 0;
  const exportLabel =
    exportMode === 'all'
      ? 'Exportar base completa'
      : `Exportar ${selectedCount} tabla${selectedCount === 1 ? '' : 's'}`;

  return (
    <div style={{ display: 'grid', gap: '14px' }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          Exportación de Datos
        </Heading>
        {showAdminBack ? <AdministrationBackLink /> : null}
      </div>

      <div style={cardStyle}>
        <p style={{ margin: '0 0 12px', fontSize: '13px', color: ui.muted, lineHeight: 1.5 }}>
          Descarga un archivo JSON (formato <code>{DUMP_FORMAT}</code>) con la base completa o solo
          las tablas que elijas. La exportación se hace por lotes (tabla a tabla, y las tablas grandes
          en tramos de {EXPORT_CHUNK_ROWS.toLocaleString('es-CL')} filas) para evitar límites del
          proxy/CDN. Luego puedes reimportarlo desde Importación.
        </p>

        <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: ui.text, cursor: 'pointer' }}>
            <input
              type="radio"
              name="export-mode"
              checked={exportMode === 'all'}
              onChange={() => setExportMode('all')}
              disabled={busy}
            />
            Base completa (todas las tablas)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: ui.text, cursor: 'pointer' }}>
            <input
              type="radio"
              name="export-mode"
              checked={exportMode === 'selected'}
              onChange={() => setExportMode('selected')}
              disabled={busy}
            />
            Seleccionar tablas
          </label>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleExport}
            disabled={busy || (exportMode === 'selected' && !selectedCount)}
            style={{
              height: '34px',
              borderRadius: '8px',
              border: 'none',
              background: ui.interactive,
              color: '#fff',
              fontWeight: 700,
              fontSize: '13px',
              padding: '0 14px',
              cursor: busy || (exportMode === 'selected' && !selectedCount) ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              opacity: busy || (exportMode === 'selected' && !selectedCount) ? 0.65 : 1,
            }}
          >
            {busy ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
            {busy ? 'Exportando…' : exportLabel}
          </button>
          <button
            type="button"
            onClick={loadSummary}
            disabled={loadingSummary || busy}
            style={{
              height: '34px',
              borderRadius: '8px',
              border: `1px solid ${ui.border}`,
              background: ui.header,
              color: ui.text,
              fontWeight: 700,
              fontSize: '13px',
              padding: '0 14px',
              cursor: loadingSummary || busy ? 'wait' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <RefreshCw size={16} />
            Actualizar resumen
          </button>
        </div>
        {error ? <p style={{ margin: '10px 0 0', fontSize: '13px', color: ui.danger }}>{error}</p> : null}
        {info ? <p style={{ margin: '10px 0 0', fontSize: '13px', color: ui.success }}>{info}</p> : null}
        {hasBlockingDates ? (
          <DateHealthWarning
            health={displayedHealth}
            ui={ui}
            title={`No se puede exportar: ${displayedHealth.invalid_count} fecha(s) corrupta(s)`}
          />
        ) : null}
        {displayedHealth?.suspicious_count > 0 && !hasBlockingDates ? (
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#b45309', lineHeight: 1.45 }}>
            Aviso: {displayedHealth.suspicious_count} fecha(s) fuera del rango operativo 1900–2100
            (exportables, pero conviene revisar).
          </p>
        ) : null}
        {summary?.schema_coverage && !summary.schema_coverage.schema_up_to_date ? (
          <p style={{ margin: '10px 0 0', fontSize: '12px', color: ui.danger || '#dc2626', lineHeight: 1.45 }}>
            Esquema incompleto respecto a migraciones 001–012
            {summary.schema_coverage.missing_expected_tables?.length
              ? `: faltan tablas ${summary.schema_coverage.missing_expected_tables.join(', ')}`
              : ''}
            {summary.schema_coverage.missing_notable_columns &&
            Object.keys(summary.schema_coverage.missing_notable_columns).length
              ? `; columnas ausentes ${Object.entries(summary.schema_coverage.missing_notable_columns)
                  .map(([table, cols]) => `${table}(${cols.join(', ')})`)
                  .join('; ')}`
              : ''}
            . Aplica las migraciones pendientes antes de exportar.
          </p>
        ) : null}
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <Database size={16} color={ui.muted} />
          <h3 style={{ margin: 0, fontSize: '14px', color: ui.text, flex: 1 }}>
            Tablas en la base
            {summary
              ? ` · ${summary.table_count} tablas · ${Number(summary.total_rows || 0).toLocaleString('es-CL')} filas`
              : ''}
            {exportMode === 'selected' ? ` · ${selectedCount} seleccionada(s)` : ''}
          </h3>
        </div>

        {exportMode === 'selected' ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10, alignItems: 'center' }}>
            <input
              type="search"
              value={tableFilter}
              onChange={(event) => setTableFilter(event.target.value)}
              placeholder="Filtrar tablas…"
              style={{
                height: 32,
                minWidth: 180,
                flex: '1 1 180px',
                borderRadius: 8,
                border: `1px solid ${ui.inputBorder || ui.border}`,
                background: ui.inputBg || ui.header,
                color: ui.text,
                padding: '0 10px',
                fontSize: 13,
              }}
            />
            <button
              type="button"
              onClick={allVisibleSelected ? clearVisible : selectAllVisible}
              disabled={!filteredTables.length || busy}
              style={{
                height: 32,
                borderRadius: 8,
                border: `1px solid ${ui.border}`,
                background: ui.header,
                color: ui.text,
                fontSize: 12,
                fontWeight: 600,
                padding: '0 10px',
                cursor: !filteredTables.length || busy ? 'not-allowed' : 'pointer',
              }}
            >
              {allVisibleSelected ? 'Quitar visibles' : 'Elegir visibles'}
            </button>
          </div>
        ) : null}

        {loadingSummary && !tables.length ? (
          <p style={{ margin: 0, fontSize: '13px', color: ui.muted }}>Cargando…</p>
        ) : !tables.length ? (
          <p style={{ margin: 0, fontSize: '13px', color: ui.muted }}>No hay tablas para mostrar.</p>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: 420 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 360 }}>
              <thead>
                <tr>
                  {exportMode === 'selected' ? (
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '6px 8px',
                        fontSize: '10px',
                        color: ui.muted,
                        borderBottom: `1px solid ${ui.border}`,
                        width: 36,
                      }}
                    >
                      {' '}
                    </th>
                  ) : null}
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: '10px',
                      color: ui.muted,
                      borderBottom: `1px solid ${ui.border}`,
                    }}
                  >
                    Tabla
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '6px 8px',
                      fontSize: '10px',
                      color: ui.muted,
                      borderBottom: `1px solid ${ui.border}`,
                    }}
                  >
                    Filas
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTables.map((table) => {
                  const checked = selectedTables.includes(table.name);
                  return (
                    <tr
                      key={table.name}
                      onClick={() => {
                        if (exportMode === 'selected' && !busy) toggleTable(table.name);
                      }}
                      style={{ cursor: exportMode === 'selected' ? 'pointer' : 'default' }}
                    >
                      {exportMode === 'selected' ? (
                        <td
                          style={{
                            padding: '6px 8px',
                            borderBottom: `1px solid ${ui.border}`,
                          }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTable(table.name)}
                            disabled={busy}
                          />
                        </td>
                      ) : null}
                      <td
                        style={{
                          padding: '6px 8px',
                          fontSize: '12px',
                          color: ui.text,
                          borderBottom: `1px solid ${ui.border}`,
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        }}
                      >
                        {table.name}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          fontSize: '12px',
                          color: ui.text,
                          borderBottom: `1px solid ${ui.border}`,
                          textAlign: 'right',
                        }}
                      >
                        {Number(table.rows || 0).toLocaleString('es-CL')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemDataExportWorkspace;
